"use client";

import type { Address } from "@/types/prisma";
import { Check, ChevronDown, CreditCard, Loader2, MapPin, Package, ShieldCheck, ShoppingCart, Truck } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { Button } from "@/components/ui/button";
import { calculateShipping } from "@/lib/delivery";
import { formatPrice } from "@/lib/utils";
import type { CheckoutStep } from "@/types";

const steps: { id: CheckoutStep; label: string; icon: typeof MapPin }[] = [
  { id: "address", label: "Address", icon: MapPin },
  { id: "delivery", label: "Delivery", icon: Truck },
  { id: "payment", label: "Payment", icon: CreditCard },
  { id: "review", label: "Review", icon: Package }
];

const CHECKOUT_DRAFT_KEY = "mova_checkout_draft";
const LAST_USED_ADDRESS_KEY = "mova_last_used_address";

function getUserDraftKey(userId?: string): string | null {
  return userId ? `${CHECKOUT_DRAFT_KEY}_${userId}` : null;
}

function getUserLastUsedKey(userId?: string): string | null {
  return userId ? `${LAST_USED_ADDRESS_KEY}_${userId}` : null;
}

type CheckoutDraft = {
  address: {
    fullName: string;
    phone: string;
    line1: string;
    line2: string;
  };
  selectedAddressId: string | null;
  step: CheckoutStep;
  deliveryMethod: string;
  paymentMethod: string;
};

function getInitialDraft(userId?: string): Partial<CheckoutDraft> | null {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const key = getUserDraftKey(userId);
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function getLastUsedAddress(userId?: string): CheckoutDraft["address"] | null {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const key = getUserLastUsedKey(userId);
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

export function CheckoutForm() {
  const [mounted, setMounted] = useState(false);
  const { data: session, status } = useSession();
  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal());
  const couponDiscount = useCartStore((s) => s.couponDiscount);

  const userId = session?.user?.id;
  // Load any previously entered draft so accidental back navigation doesn't wipe progress
  const draft = useMemo(() => getInitialDraft(userId), [userId]);
  // Or load address used on first/previous purchase
  const lastUsed = useMemo(() => getLastUsedAddress(userId), [userId]);

  const [step, setStep] = useState<CheckoutStep>(() => {
    if (draft?.step && steps.some((s) => s.id === draft.step)) {
      return draft.step;
    }
    return "address";
  });
  const [loading, setLoading] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState(() => draft?.deliveryMethod ?? "standard");
  const [paymentMethod, setPaymentMethod] = useState(() => draft?.paymentMethod ?? "cod");
  const [address, setAddress] = useState(() => ({
    fullName: draft?.address?.fullName || lastUsed?.fullName || session?.user?.name || "",
    phone: draft?.address?.phone || lastUsed?.phone || "",
    line1: draft?.address?.line1 || lastUsed?.line1 || "",
    line2: draft?.address?.line2 || lastUsed?.line2 || ""
  }));
  const [savedAddresses, setSavedAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(() => draft?.selectedAddressId ?? null);
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({});
  const [showMobileSummary, setShowMobileSummary] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Purge legacy un-scoped localStorage keys
    try {
      localStorage.removeItem(CHECKOUT_DRAFT_KEY);
      localStorage.removeItem(LAST_USED_ADDRESS_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Save draft whenever address or checkout options change (scoped to logged-in user)
  useEffect(() => {
    if (!mounted || !session?.user?.id) return;
    try {
      const currentDraft: CheckoutDraft = {
        address,
        selectedAddressId,
        step,
        deliveryMethod,
        paymentMethod
      };
      const key = getUserDraftKey(session.user.id);
      if (key) localStorage.setItem(key, JSON.stringify(currentDraft));
    } catch {
      // ignore
    }
  }, [mounted, session?.user?.id, address, selectedAddressId, step, deliveryMethod, paymentMethod]);

  useEffect(() => {
    if (!session?.user) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/addresses");
        if (!response.ok) return;
        const data = (await response.json()) as { addresses: Address[] };
        if (cancelled) return;

        setSavedAddresses(data.addresses);
        const preferred = data.addresses.find((a) => a.isDefault) ?? data.addresses[0];
        if (preferred) {
          setSelectedAddressId(preferred.id);
          setAddress((current) => {
            // Keep user manual input only if they're actively typing something different
            const isActivelyDifferent = Boolean(
              current.fullName.trim() &&
              current.fullName !== preferred.fullName &&
              current.line1 !== preferred.line1
            );
            if (isActivelyDifferent) return current;

            return {
              fullName: preferred.fullName,
              phone: preferred.phone,
              line1: preferred.line1,
              line2: preferred.line2 ?? ""
            };
          });
        } else {
          // Newly registered customer with no saved addresses:
          // Default name to the newly registered customer's name!
          const userDraft = getInitialDraft(session.user.id);
          const userLast = getLastUsedAddress(session.user.id);

          setAddress((current) => {
            if (userDraft?.address?.fullName) {
              return { ...userDraft.address };
            }
            if (userLast?.fullName) {
              return { ...userLast };
            }
            return {
              fullName: session.user?.name || current.fullName || "",
              phone: current.phone || "",
              line1: current.line1 || "",
              line2: current.line2 || "",
            };
          });
        }
      } catch {
        // saved addresses are a convenience; manual entry still works
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user]);

  function selectSavedAddress(saved: Address) {
    setSelectedAddressId(saved.id);
    setAddressErrors({});
    setAddress({
      fullName: saved.fullName,
      phone: saved.phone,
      line1: saved.line1,
      line2: saved.line2 ?? ""
    });
  }

  function validateAddress(): boolean {
    const errors: Record<string, string> = {};
    if (!address.fullName.trim()) {
      errors.fullName = "Full name is required";
    } else if (address.fullName.trim().length < 2) {
      errors.fullName = "Full name must be at least 2 characters";
    }

    if (!address.phone.trim()) {
      errors.phone = "Mobile phone number is required";
    } else if (address.phone.trim().replace(/\s+/g, "").length < 8) {
      errors.phone = "Mobile number must be at least 8 digits";
    }

    if (!address.line1.trim()) {
      errors.line1 = "Delivery Address / Street is required";
    } else if (address.line1.trim().length < 4) {
      errors.line1 = "Delivery Address / Street must be at least 4 characters";
    }

    setAddressErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill in all required delivery address fields.");
      return false;
    }
    return true;
  }

  function handleContinueToPayment(event?: FormEvent) {
    if (event) event.preventDefault();
    if (!validateAddress()) return;
    setStep("payment");
  }

  const shipping = calculateShipping(subtotal, true);
  const total = subtotal - couponDiscount + shipping;

  const stepIndex = steps.findIndex((s) => s.id === step);

  async function placeOrder(event: FormEvent) {
    event.preventDefault();
    if (!session?.user) {
      toast.error("Please sign in before checkout.");
      return;
    }
    if (!validateAddress()) {
      setStep("address");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((item) => ({
            productId: item.product.id,
            quantity: item.quantity,
            variantId: item.selectedVariant?.id || undefined,
          })),
          address,
          deliveryMethod,
          paymentMethod,
          couponCode: useCartStore.getState().couponCode
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? "Checkout failed");
      try {
        if (session?.user?.id) {
          const lastKey = getUserLastUsedKey(session.user.id);
          if (lastKey) localStorage.setItem(lastKey, JSON.stringify(address));
          const draftKey = getUserDraftKey(session.user.id);
          if (draftKey) localStorage.removeItem(draftKey);
        }
        localStorage.removeItem(LAST_USED_ADDRESS_KEY);
        localStorage.removeItem(CHECKOUT_DRAFT_KEY);
      } catch {
        // ignore
      }
      if (data.url) window.location.href = data.url;
      else if (data.orderId) window.location.href = `/checkout/success?orderId=${data.orderId}`;
      else window.location.href = "/checkout/success";
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Checkout failed");
    } finally {
      setLoading(false);
    }
  }



  if (!mounted) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-center">
        <div className="mx-auto h-8 w-48 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-center">
        <h1 className="text-3xl font-bold">No items to checkout</h1>
        <Link href="/search" className="amazon-btn-primary mt-6 inline-flex">
          Continue shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-3 py-4 sm:px-5 sm:py-8">
      {/* Mobile-First Collapsible Order Summary Accordion */}
      <div className="lg:hidden mb-4 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xs dark:border-white/10 dark:bg-slate-900">
        <button
          type="button"
          onClick={() => setShowMobileSummary((v) => !v)}
          className="flex w-full items-center justify-between p-3.5 text-left transition hover:bg-slate-50 dark:hover:bg-white/5"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#087DB5]/10 text-[#087DB5] dark:bg-white/10 dark:text-[#00B7B0]">
              <ShoppingCart className="h-4 w-4" />
            </div>
            <div>
              <span className="flex items-center gap-1 text-xs font-bold text-slate-800 dark:text-slate-200">
                <span>{showMobileSummary ? "Hide order summary" : "Show order summary"}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMobileSummary ? "rotate-180" : ""}`} />
              </span>
              <span className="text-[11px] text-slate-500">{items.reduce((acc, i) => acc + i.quantity, 0)} items in cart</span>
            </div>
          </div>
          <span className="text-base font-bold text-[#073B70] dark:text-white">
            {formatPrice(total)}
          </span>
        </button>

        {showMobileSummary && (
          <div className="border-t border-slate-100 bg-slate-50/50 p-3.5 dark:border-white/10 dark:bg-slate-900/50">
            <div className="max-h-48 space-y-2.5 overflow-y-auto pr-1">
              {items.map((item) => {
                const itemPrice = item.selectedVariant?.price ?? item.product.price;
                const itemImg = item.selectedVariant?.image || item.product.images[0] || "/placeholder.png";
                const key = `${item.product.id}-${item.selectedVariant?.id || "base"}`;
                return (
                  <div key={key} className="flex items-center gap-2.5">
                    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-slate-200/70 bg-white dark:border-white/10">
                      <Image src={itemImg} alt="" fill sizes="48px" className="object-contain p-0.5" />
                    </div>
                    <div className="min-w-0 flex-1 text-xs">
                      <p className="line-clamp-1 font-semibold text-slate-900 dark:text-white">{item.product.title}</p>
                      {item.selectedVariant && (
                        <p className="text-[11px] text-slate-500 truncate">
                          {item.selectedVariant.title || item.selectedVariant.color}
                        </p>
                      )}
                      <p className="text-slate-400">Qty {item.quantity}</p>
                    </div>
                    <span className="text-xs font-bold text-[#073B70] dark:text-white shrink-0">
                      {formatPrice(itemPrice * item.quantity)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 space-y-1.5 border-t border-slate-200/80 pt-2.5 text-xs dark:border-white/10">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Subtotal</span>
                <span className="font-bold text-slate-900 dark:text-white">{formatPrice(subtotal)}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between text-[#00B7B0] font-semibold">
                  <span>Coupon</span>
                  <span>-{formatPrice(couponDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Shipping</span>
                <span className="font-bold text-slate-900 dark:text-white">{shipping === 0 ? "FREE" : formatPrice(shipping)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-slate-200/80 pt-2 text-[#073B70] dark:border-white/10 dark:text-white">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progress Steps */}
      <nav className="mb-5 flex items-center gap-1.5 overflow-x-auto pb-1.5 no-scrollbar sm:flex-wrap sm:gap-2 sm:pb-0">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const done = i < stepIndex;
          const current = s.id === step;
          return (
            <div key={s.id} className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <div
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold sm:px-3.5 sm:py-2 sm:text-sm ${
                  current
                    ? "bg-gradient-to-r from-[#087DB5] to-[#073B70] text-white shadow-xs"
                    : done
                    ? "bg-[#00B7B0]/20 text-[#073B70] dark:text-teal-300"
                    : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#00B7B0]" /> : <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                {s.label}
              </div>
              {i < steps.length - 1 && <span className="text-xs text-slate-300 sm:text-sm">→</span>}
            </div>
          );
        })}
      </nav>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-slate-900 sm:p-6">
          {status !== "loading" && !session?.user && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs sm:text-sm dark:bg-amber-950/20 dark:border-amber-500/30">
              <Link href="/login?callbackUrl=/checkout" className="font-bold text-[#087DB5] hover:text-[#073B70] underline">
                Sign in
              </Link>{" "}
              to place your order with saved addresses and faster checkout.
            </div>
          )}

          {step === "address" && (
            <div>
              <div className="flex items-center justify-between">
                <h1 className="text-xl sm:text-2xl font-bold text-[#073B70] dark:text-white">Delivery address</h1>
                <span className="text-xs text-slate-500 hidden sm:inline">Minimal typing required</span>
              </div>

              {savedAddresses.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Saved Addresses</p>
                  {savedAddresses.map((sa) => {
                    const isSelected = selectedAddressId === sa.id;
                    return (
                      <label
                        key={sa.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 text-sm transition-all ${
                          isSelected
                            ? "border-[#087DB5] bg-sky-50/50 shadow-xs dark:bg-slate-800/80"
                            : "border-slate-200 bg-white hover:border-slate-300 dark:border-white/10 dark:bg-slate-900"
                        }`}
                      >
                        <input
                          type="radio"
                          name="savedAddress"
                          className="mt-1 h-4 w-4 accent-[#087DB5]"
                          checked={isSelected}
                          onChange={() => selectSavedAddress(sa)}
                        />
                        <div className="min-w-0 flex-1">
                          <span className="block font-bold text-[#073B70] dark:text-white">{sa.fullName}</span>
                          <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                            {sa.line1}
                            {sa.line2 ? `, ${sa.line2}` : ""}
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">{sa.phone}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Manual entry with Minimal Typing Quick Pills */}
              <form onSubmit={handleContinueToPayment} className="mt-5">
                {savedAddresses.length > 0 && (
                  <p className="mb-3 text-xs font-bold text-slate-500 uppercase tracking-wide">
                    Or Enter a New Delivery Address
                  </p>
                )}
                <div className="grid gap-3.5 sm:grid-cols-2">
                  {/* Full Name */}
                  <label className="sm:col-span-1">
                    <span className="flex items-center text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                      Full name <span className="ml-1 text-red-500 font-bold">*</span>
                    </span>
                    <input
                      required
                      type="text"
                      autoComplete="name"
                      autoCapitalize="words"
                      placeholder="e.g. Abebe Kebede"
                      value={address.fullName}
                      onChange={(e) => {
                        setSelectedAddressId(null);
                        setAddress((a) => ({ ...a, fullName: e.target.value }));
                        if (addressErrors.fullName) {
                          setAddressErrors((errs) => {
                            const updated = { ...errs };
                            delete updated.fullName;
                            return updated;
                          });
                        }
                      }}
                      className={`mt-1 h-11 w-full rounded-xl border px-3 text-base sm:text-sm outline-none transition-colors dark:bg-slate-950 ${
                        addressErrors.fullName
                          ? "border-red-500 ring-1 ring-red-500"
                          : "border-slate-200 focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10"
                      }`}
                    />
                    {addressErrors.fullName && (
                      <p className="mt-1 text-xs font-semibold text-red-500">{addressErrors.fullName}</p>
                    )}
                  </label>

                  {/* Phone */}
                  <label className="sm:col-span-1">
                    <span className="flex items-center text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                      Mobile number <span className="ml-1 text-red-500 font-bold">*</span>
                    </span>
                    <input
                      required
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="+251 91 234 5678"
                      value={address.phone}
                      onChange={(e) => {
                        setSelectedAddressId(null);
                        setAddress((a) => ({ ...a, phone: e.target.value }));
                        if (addressErrors.phone) {
                          setAddressErrors((errs) => {
                            const updated = { ...errs };
                            delete updated.phone;
                            return updated;
                          });
                        }
                      }}
                      className={`mt-1 h-11 w-full rounded-xl border px-3 text-base sm:text-sm outline-none transition-colors dark:bg-slate-950 ${
                        addressErrors.phone
                          ? "border-red-500 ring-1 ring-red-500"
                          : "border-slate-200 focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10"
                      }`}
                    />
                    {addressErrors.phone && (
                      <p className="mt-1 text-xs font-semibold text-red-500">{addressErrors.phone}</p>
                    )}
                  </label>

                  {/* Address Line 1 */}
                  <label className="sm:col-span-2">
                    <span className="flex items-center text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                      Delivery Address / Street <span className="ml-1 text-red-500 font-bold">*</span>
                    </span>
                    <input
                      required
                      type="text"
                      autoComplete="street-address"
                      placeholder="e.g. Bole Medhanialem, Street address, Addis Ababa"
                      value={address.line1}
                      onChange={(e) => {
                        setSelectedAddressId(null);
                        setAddress((a) => ({ ...a, line1: e.target.value }));
                        if (addressErrors.line1) {
                          setAddressErrors((errs) => {
                            const updated = { ...errs };
                            delete updated.line1;
                            return updated;
                          });
                        }
                      }}
                      className={`mt-1 h-11 w-full rounded-xl border px-3 text-base sm:text-sm outline-none transition-colors dark:bg-slate-950 ${
                        addressErrors.line1
                          ? "border-red-500 ring-1 ring-red-500"
                          : "border-slate-200 focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10"
                      }`}
                    />
                    {addressErrors.line1 && (
                      <p className="mt-1 text-xs font-semibold text-red-500">{addressErrors.line1}</p>
                    )}
                  </label>

                  {/* Address Line 2 */}
                  <label className="sm:col-span-2">
                    <span className="flex items-center text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                      Landmark / Apartment / Notes <span className="ml-1 text-xs font-normal text-slate-400">(Optional)</span>
                    </span>
                    <input
                      type="text"
                      autoComplete="address-line2"
                      placeholder="e.g. Near Edna Mall, 2nd floor"
                      value={address.line2}
                      onChange={(e) => {
                        setSelectedAddressId(null);
                        setAddress((a) => ({ ...a, line2: e.target.value }));
                      }}
                      className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-base sm:text-sm outline-none transition-colors focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-950"
                    />
                  </label>
                </div>

                <div className="mt-6">
                  <Button type="submit" className="w-full sm:w-auto h-12 px-8 text-base font-bold shadow-md">
                    Continue to Payment →
                  </Button>
                </div>
              </form>
            </div>
          )}

          {step === "payment" && (
            <div>
              <h1 className="text-2xl font-bold text-[#073B70] dark:text-white">Payment method</h1>
              <p className="mt-1 text-sm text-slate-500">Cash on delivery available across Ethiopia. Digital and mobile banking options coming soon.</p>
              <div className="mt-4 space-y-3">
                {[
                  { id: "cod", label: "Cash on Delivery (COD)", disabled: false },
                  { id: "telebirr", label: "Telebirr", disabled: true },
                  { id: "cbe", label: "Commercial Bank of Ethiopia (CBE / CBE Birr)", disabled: true }
                ].map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-3 rounded-xl border p-4 transition-all ${
                      paymentMethod === opt.id
                        ? "border-2 border-[#087DB5] bg-sky-50/50 dark:bg-sky-950/20"
                        : "border-slate-200 dark:border-white/10"
                    } ${opt.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      className="accent-[#087DB5]"
                      disabled={opt.disabled}
                      checked={paymentMethod === opt.id}
                      onChange={() => setPaymentMethod(opt.id)}
                    />
                    <span className="font-bold text-slate-900 dark:text-white">{opt.label}</span>
                    {opt.disabled && <span className="text-xs text-slate-400 font-medium">(Coming soon)</span>}
                  </label>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <Button variant="outline" onClick={() => setStep("address")}>
                  Back
                </Button>
                <Button onClick={() => setStep("review")}>Review order</Button>
              </div>
            </div>
          )}


          {step === "review" && (
            <form onSubmit={placeOrder}>
              <h1 className="text-2xl font-bold text-[#073B70] dark:text-white">Review your order</h1>
              <div className="mt-4 rounded-xl border border-slate-200/80 bg-slate-50/60 p-4 text-sm dark:border-white/10 dark:bg-slate-800/40">
                <p className="font-bold text-[#073B70] dark:text-white">Deliver to</p>
                <p className="mt-1 text-slate-600 dark:text-slate-300">
                  {address.fullName}, {address.line1}
                  {address.line2 ? `, ${address.line2}` : ""}
                </p>
                <p className="mt-2 text-slate-700 dark:text-slate-300">
                  <strong className="text-slate-900 dark:text-white">Payment:</strong> {paymentMethod === "cod" ? "Cash on Delivery (COD)" : paymentMethod}
                </p>
              </div>
              <div className="mt-6 flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep("payment")}>
                  Back
                </Button>
                <Button type="submit" disabled={loading || !session?.user}>
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Place order
                </Button>
              </div>
            </form>
          )}
        </div>

        <aside className="h-fit rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900">
          <h2 className="text-xl font-bold text-[#073B70] dark:text-white">Order summary</h2>
          <div className="mt-4 max-h-56 space-y-3 overflow-y-auto pr-1">
            {items.map((item) => {
              const itemPrice = item.selectedVariant?.price ?? item.product.price;
              const itemImg = item.selectedVariant?.image || item.product.images[0] || "/placeholder.png";
              const key = `${item.product.id}-${item.selectedVariant?.id || "base"}`;
              return (
                <div key={key} className="flex gap-3 items-center">
                  <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-100 bg-slate-50 dark:border-white/5">
                    <Image src={itemImg} alt="" fill sizes="56px" className="object-contain p-1" />
                  </div>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="line-clamp-1 font-semibold text-slate-900 dark:text-white">{item.product.title}</p>
                    {item.selectedVariant && (
                      <p className="text-xs text-slate-500">
                        Variant: <span className="font-medium text-slate-700 dark:text-slate-300">{item.selectedVariant.title || item.selectedVariant.color}</span>
                        {item.selectedVariant.sku && <span className="ml-1 text-[11px] text-slate-400">({item.selectedVariant.sku})</span>}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">Qty {item.quantity}</p>
                  </div>
                  <strong className="text-sm font-bold text-[#073B70] dark:text-white">{formatPrice(itemPrice * item.quantity)}</strong>
                </div>
              );
            })}
          </div>

          <div className="mt-4 space-y-2 border-t border-slate-100 pt-4 text-sm dark:border-white/10">
            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Subtotal</span>
              <strong className="text-slate-900 dark:text-white">{formatPrice(subtotal)}</strong>
            </div>
            {couponDiscount > 0 && (
              <div className="flex justify-between text-[#00B7B0] font-semibold">
                <span>Coupon</span>
                <strong>-{formatPrice(couponDiscount)}</strong>
              </div>
            )}
            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Shipping</span>
              <strong className="text-slate-900 dark:text-white">{shipping === 0 ? "FREE" : formatPrice(shipping)}</strong>
            </div>
            <div className="flex justify-between text-base font-bold border-t border-slate-100 pt-2 dark:border-white/10">
              <span className="text-[#073B70] dark:text-white">Total</span>
              <span className="text-xl text-[#073B70] dark:text-white">{formatPrice(total)}</span>
            </div>
          </div>
          <div className="mt-4 flex gap-2 rounded-xl border border-teal-200/70 bg-teal-50/50 p-3 text-xs text-slate-700 dark:border-teal-500/20 dark:bg-slate-800/60 dark:text-slate-300">
            <ShieldCheck className="h-4 w-4 shrink-0 text-[#00B7B0] mt-0.5" />
            <span>Secure MOVA checkout · Ethiopian nationwide guarantee</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
